import { useEffect, useMemo } from 'react';
import { useClientId } from '../../../../../../_hooks/useClientId';
import { useFpId } from '../../../../../hooks/useFpId';
import { useMarketScenariosQuery } from '../../../../api/useMarketScenariosQuery';
import {
  BodyParams,
  useMarketScenariosWhatIfsMutation,
} from '../../../../api/useMarketScenariosWhatIfsMutation';
import { MonteCarloPercentilesDTO, PercentilesByYearDTO } from '../../../../types/monteCarlo.types';
import {
  MARKET_CONDITIONS,
  MarketScenarioLabel,
  ScenarioProjectionData,
  YearlyResult,
} from '../../tabs/MonteCarlo/MonteCarlo.meta';

function percentileValue(row: PercentilesByYearDTO, percentile: number): number {
  switch (percentile) {
    case 75:
      return row.Percentile75;
    case 50:
      return row.Percentile50;
    case 25:
      return row.Percentile25;
    case 10:
      return row.Percentile10;
    default:
      return row.Percentile50;
  }
}

function buildScenarioProjectionFromPercentiles(
  percentileData: MonteCarloPercentilesDTO,
): ScenarioProjectionData {
  const sortedData = [...(percentileData.PercentilesByYear ?? [])].sort((a, b) => a.Year - b.Year);
  const categories = sortedData.map(p => String(p.Year));

  const series = MARKET_CONDITIONS.map(condition => ({
    name: condition.category,
    color: condition.color,
    data: sortedData.map(row => percentileValue(row, condition.percentile)),
  }));

  const last = sortedData[sortedData.length - 1];
  const stats = MARKET_CONDITIONS.map(condition => ({
    label: condition.category,
    color: condition.color,
    value: last ? percentileValue(last, condition.percentile) : 0,
    isFocused: condition.category === MarketScenarioLabel.Average,
  }));

  return { series, categories, stats };
}

function buildYearlyResultsFromPercentiles(
  percentileData: MonteCarloPercentilesDTO,
): YearlyResult[] {
  const sortedData = [...(percentileData.PercentilesByYear ?? [])].sort((a, b) => a.Year - b.Year);

  return sortedData.map(row => ({
    year: row.Year,
    principalAge: row.PrincipalAge,
    spouseAge: row.SpouseAge,
    significantlyBelowAverage: row.Percentile10,
    belowAverage: row.Percentile25,
    average: row.Percentile50,
    aboveAverage: row.Percentile75,
  }));
}

interface UseMarketScenariosParams {
  whatIfsBody?: BodyParams | null;
  isWhatIfsActive?: boolean;
  isPrint?: boolean;
}

export const useMarketScenarios = ({
  whatIfsBody = null,
  isWhatIfsActive = false,
  isPrint = false,
}: UseMarketScenariosParams = {}) => {
  const clientId = useClientId();
  const fpId = useFpId();

  const {
    isFetching: isQueryFetching,
    data: queryData,
    error: queryError,
    refetch,
  } = useMarketScenariosQuery({
    clientId,
    fpId,
    enabled: !isWhatIfsActive,
  });

  const mutation = useMarketScenariosWhatIfsMutation({ clientId, fpId });

  useEffect(() => {
    if (isWhatIfsActive && !isPrint && whatIfsBody != null) {
      // The previous in-flight request is aborted automatically via the shared
      // abort registry (keyed by the /Consumer/MC/Percentiles endpoint).
      mutation.mutate({ clientId, fpId, body: whatIfsBody });
    }
  }, [isWhatIfsActive, whatIfsBody, isPrint]);

  const isLoading = isWhatIfsActive ? mutation.isLoading : isQueryFetching;
  const marketScenariosData = isWhatIfsActive ? mutation.data : queryData;
  const error = isWhatIfsActive ? mutation.error : queryError;

  const data = useMemo(() => {
    if (!marketScenariosData?.PercentilesByYear?.length) {
      return null;
    }

    return {
      milestoneData: { ...marketScenariosData },
      scenarioProjection: buildScenarioProjectionFromPercentiles(marketScenariosData),
      yearlyResults: buildYearlyResultsFromPercentiles(marketScenariosData),
    };
  }, [marketScenariosData]);

  return {
    isLoading,
    data,
    error,
    refetch,
  };
};
